import { Box, Group, Stack } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import { Text } from '@repo/foundations';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { LiteSecodaEntity } from '../../../../../../../packages/api-codegen';
import { useAuthUser, type ISecodaEntity } from '../../../../api';
import { getEntityDisplayTitle } from '../../../../lib/utils/entity';
import { buildResourceUrl } from '../../../../utils/navigationUtils';
import { ResourceLinkType } from '../../../Link/useResourceLink';
import { ResourcePreview } from '../../../ResourcePreview';
import { SecodaEntityIcon } from '../../../SecodaEntity';
import { useStyles } from './styles';
import { validateEntityType } from './utils';

interface IRelatedEntityProps {
	entity: ISecodaEntity | LiteSecodaEntity;
	hoverButton?: ReactNode;
	rightButton?: ReactNode;
	withinPortal?: boolean;
}

export function EntityDisplay({
	entity,
	hoverButton,
	rightButton,
	withinPortal,
}: IRelatedEntityProps) {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const { user, workspace } = useAuthUser();

	const handleClick = () => {
		trackEvent(
			'related_resource/sidebar/click',
			{
				entity_id: entity.id,
			},
			user,
			workspace
		);
		navigate(
			buildResourceUrl({
				id: entity.id,
				entity_type: validateEntityType(entity.entity_type),
			})
		);
	};

	const breadcrumbs = getSummaryAndBreadCrumbs(
		entity.native_type || entity.entity_type || null,
		entity.search_metadata
	);

	const getIntegrationId = () => {
		if (!('integration' in entity)) return undefined;
		const { integration } = entity;
		if (!integration) return undefined;
		if (typeof integration === 'string') return integration;
		return 'id' in integration ? integration.id : undefined;
	};

	return (
		<ResourcePreview
			data={entity}
			id={entity.id}
			resourceType={ResourceLinkType.Entity}
			isLoading={false}
			hasError={false}
			position="left-start"
			width={300}
			withinPortal={withinPortal}
		>
			<Group spacing="xs" className={classes.itemWrapper} noWrap>
				<Group
					spacing="xs"
					position="apart"
					noWrap
					className={classes.entityLink}
					onClick={handleClick}
					style={{ flex: 1 }}
				>
					<Group spacing="xs" align="flex-start" noWrap>
						<Box className={classes.iconWrapper}>
							<SecodaEntityIcon
								entity={{
									id: entity.id,
									icon: entity.icon || undefined,
									entity_type: validateEntityType(entity.entity_type),
									integration: getIntegrationId(),
									title: entity.title || '',
								}}
								inline
								// NOTE: Keep this consistent with Icon.tsx value
								size={20}
							/>
						</Box>
						<Stack spacing={0}>
							<Text size="sm" lineClamp={1} className={classes.label}>
								{getEntityDisplayTitle(entity)}
							</Text>
							<Text size="xs" color="text/secondary/default" lineClamp={1}>
								{breadcrumbs}
							</Text>
						</Stack>
					</Group>
					{hoverButton && (
						<Box className={classes.buttonWrapper}>{hoverButton}</Box>
					)}
				</Group>
				{rightButton && (
					<Box className={classes.buttonWrapper}>{rightButton}</Box>
				)}
			</Group>
		</ResourcePreview>
	);
}
